import { Collapse, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import * as types from '@models/types';

import {
    FC, useEffect, useState
} from 'react';

import './UnemployedDrillDownTable.scss';


type UnemployedDrillDownTableProps = {
    data?: types.UnemployedJobPostsTypes
};

const UnemployedDrillDownTable: FC<UnemployedDrillDownTableProps> = ({
    data
}) => {
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(5);

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - (data && data.jobs_unemployed ? data.jobs_unemployed.length : 0)) : 0;

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return <div>
        {data && data.jobs_unemployed && data.jobs_unemployed.length > 0 &&
            <>
                <TableContainer className="unemployed_container">
                    <Table aria-label="collapsible table" className="unemployed_table">
                        <TableHead>
                            <TableRow>
                                <TableCell className="width-5" />
                                <TableCell>Specialization</TableCell>
                                <TableCell align="right" className="width-15">Unemployed People</TableCell>
                                <TableCell align="right" className="width-15">Job Posts</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(rowsPerPage > 0
                                ? data.jobs_unemployed.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                : data.jobs_unemployed
                            ).map((specialization: types.JobsUnemployedTypes, index: number) => {
                                return <SpecializationRow key={"specialization-" + index} data={specialization} currentPage={page} />
                            })}
                            {emptyRows > 0 && (
                                <TableRow style={{ height: 53 * emptyRows }}>
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    className="unemployed_table_pagination"
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={data.jobs_unemployed.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </>
        }
    </div>
};

export default UnemployedDrillDownTable;


type SpecializationRowProps = {
    data?: types.JobsUnemployedTypes,
    currentPage: number
};

const SpecializationRow: FC<SpecializationRowProps> = ({
    data,
    currentPage
}) => {
    const [openEducationLevels, setOpenEducationLevels] = useState<boolean>(false);

    // On change page collapse all opened education levels
    useEffect(() => {
        setOpenEducationLevels(false);
    }, [currentPage]);

    return <>
        {data &&
            <>
                <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                    <TableCell className="width-5">
                    {
                        data.education_levels && data.education_levels.length > 0 &&
                        <IconButton
                            aria-label="expand row"
                            size="small"
                            onClick={() => setOpenEducationLevels(!openEducationLevels)}
                            className="drilldown_button"
                        >
                            {openEducationLevels ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                    }
                    </TableCell>
                    <TableCell scope="row">{data.isced4_label}</TableCell>
                    <TableCell align="right" className="width-15">{data.count_unemployed_people}</TableCell>
                    <TableCell align="right" className="width-15">{data.count_jobs}</TableCell>
                </TableRow>
                {
                    data.education_levels && data.education_levels.length > 0 &&
                    <TableRow>
                        <TableCell className="zero-padding" colSpan={6}>
                            <Collapse in={openEducationLevels} timeout="auto" unmountOnExit>
                                <TableContainer>
                                    <Table aria-label="collapsible table" className="education_level_table">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell className="width-5" />
                                                <TableCell>Education Level</TableCell>
                                                <TableCell align="right" className="width-15 education_level_unemployed_people">Unemployed People</TableCell>
                                                <TableCell align="right" className="width-15">Job Posts</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {
                                                data.education_levels.map((education: types.EducationLevelsTypes, index: number) => {
                                                    return <EducationLevelRow key={"educationLevel-" + index} data={education} currentPage={currentPage} />
                                                })
                                            }
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Collapse>
                        </TableCell>
                    </TableRow>
                }
            </>
        }
    </>
};


type EducationLevelRowProps = {
    data?: types.EducationLevelsTypes,
    currentPage: number
};

const EducationLevelRow: FC<EducationLevelRowProps> = ({
    data,
    currentPage
}) => {
    const [openJobs, setOpenJobs] = useState<boolean>(false);

    // On change page collapse all opened jobs
    useEffect(() => {
        setOpenJobs(false);
    }, [currentPage]);

    return <>
        {data &&
            <>
                <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                    <TableCell className="width-5">
                    {
                        data.job_posts && data.job_posts.length > 0 &&
                        <IconButton
                            aria-label="expand row"
                            size="small"
                            onClick={() => setOpenJobs(!openJobs)}
                            className="drilldown_button"
                        >
                            {openJobs ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                    }
                    </TableCell>
                    <TableCell scope="row">{data.education_level}</TableCell>
                    <TableCell align="right" className="width-15 education_level_unemployed_people">{data.count_unemployed_people}</TableCell>
                    <TableCell align="right" className="width-15">{data.count_jobs}</TableCell>
                </TableRow>
                {
                    data.job_posts && data.job_posts.length > 0 &&
                    <TableRow>
                        <TableCell className="zero-padding" colSpan={6}>
                            <Collapse in={openJobs} timeout="auto" unmountOnExit>
                                <TableContainer>
                                    <Table aria-label="collapsible table" className="jobs_table">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Job Title</TableCell>
                                                <TableCell align="right" className="width-15">Job Posts</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {
                                                data.job_posts.map((job: types.JobPostTypes, index: number) => {
                                                    return <JobRow key={"job-" + index} data={job} />
                                                })
                                            }
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Collapse>
                        </TableCell>
                    </TableRow>
                }
            </>
        }
    </>
};


type JobRowProps = {
    data?: types.JobPostTypes
};

const JobRow: FC<JobRowProps> = ({
    data
}) => {
    return <>
        {data &&
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell scope="row">{data.job_title}</TableCell>
                <TableCell align="right" className="width-15">{data.count_jobs}</TableCell>
            </TableRow>
        }
    </>
};